<template>
  <div class="lp-root">

    <!-- ─── NAVBAR ─── -->
    <nav class="lp-nav" :class="{ 'lp-nav--scrolled': scrolled }">
      <div class="lp-nav-inner">
        <!-- Brand -->
        <div class="lp-nav-brand">
          <img src="../assets/Images/pnglogo.png" alt="Logo" class="lp-nav-logo" />
          <div class="lp-nav-brand-text">
            <span class="lp-nav-name">NATURESTAY</span>
            <span class="lp-nav-dept">Kerala Forest Department</span>
          </div>
        </div>
        <!-- Links -->
        <div class="lp-nav-links">
          <a href="#features" class="lp-nav-link">Features</a>
          <a href="#about" class="lp-nav-link">About</a>
          <a href="#contact" class="lp-nav-link">Contact</a>
          <router-link to="/how-to-apply" class="lp-nav-link">How to Apply</router-link>
          <router-link to="/login" class="lp-nav-btn">
            <v-icon small style="margin-right:5px;">mdi-login-variant</v-icon>
            Sign In
          </router-link>
          <button type="button" class="lp-nav-burger" :aria-label="menuOpen ? 'Close menu' : 'Open menu'" :aria-expanded="menuOpen" @click="menuOpen = !menuOpen">
            <v-icon color="#fff">{{ menuOpen ? 'mdi-close' : 'mdi-menu' }}</v-icon>
          </button>
        </div>
      </div>
      <!-- Mobile menu -->
      <transition name="lp-menu">
        <div v-if="menuOpen" class="lp-mobile-menu">
          <a href="#features" class="lp-mobile-link" @click="menuOpen = false"><v-icon small>mdi-star-four-points-outline</v-icon>Features</a>
          <a href="#about" class="lp-mobile-link" @click="menuOpen = false"><v-icon small>mdi-information-outline</v-icon>About</a>
          <a href="#contact" class="lp-mobile-link" @click="menuOpen = false"><v-icon small>mdi-phone-outline</v-icon>Contact</a>
          <router-link to="/how-to-apply" class="lp-mobile-link" @click.native="menuOpen = false"><v-icon small>mdi-file-document-outline</v-icon>How to Apply</router-link>
        </div>
      </transition>
    </nav>

    <!-- ─── HERO ─── -->
    <section class="lp-hero" id="home">
      <div class="lp-hero-bg">
        <img src="../assets/Images/kerala_forest_hero.jpg" alt="Kerala Forest" class="lp-hero-img" />
        <div class="lp-hero-overlay"></div>
        <!-- Gradient strips -->
        <div class="lp-hero-vignette"></div>
      </div>

      <div class="lp-hero-content">
        <div class="lp-hero-grid">
          <!-- LEFT: Text -->
          <div class="lp-hero-text">
            <!-- Government Badge -->
            <div class="lp-gov-banner">
              <span class="lp-kfd-badge lp-kfd-badge--hero"><img src="../assets/Images/blacklogo.png" alt="Kerala Forests" /></span>
              <div class="lp-gov-text">
                <span class="lp-gov-title">Kerala Forest Department</span>
                <span class="lp-gov-sub">Government of Kerala — Nature Camp Division</span>
              </div>
            </div>

            <!-- Main headline -->
            <h1 class="lp-hero-title">
              <span class="lp-title-line1">Discover</span>
              <span class="lp-title-line2">Kerala's<br/><em>Wild Soul</em></span>
            </h1>

            <p class="lp-hero-desc">
              The official nature camp booking portal of the Kerala Forest Department. Reserve your stay in the heart of God's Own Country — pristine forests, misty hills, and untamed biodiversity await.
            </p>

            <!-- CTA Buttons -->
            <div class="lp-hero-cta">
              <router-link to="/login" class="lp-btn-primary">
                <v-icon style="margin-right:8px;">mdi-tent</v-icon>
                Book a Nature Camp
              </router-link>
              <a href="#features" class="lp-btn-ghost">
                Explore Features
                <v-icon style="margin-left:6px;" small>mdi-arrow-down</v-icon>
              </a>
            </div>

            <!-- Stats row -->
            <div class="lp-stats-row">
              <div class="lp-stat-pill" v-for="s in stats" :key="s.label">
                <v-icon small :color="s.color">{{ s.icon }}</v-icon>
                <span class="lp-stat-val">{{ s.value }}</span>
                <span class="lp-stat-lbl">{{ s.label }}</span>
              </div>
            </div>
          </div>

          <!-- RIGHT: Floating image card -->
          <div class="lp-hero-visual d-none d-md-flex">
            <div class="lp-glass-wrap">
              <div class="lp-glass-chip lp-glass-chip--top">
                <v-icon small color="#fbbf24">mdi-shield-check</v-icon> Official Eco-Camps
              </div>
              <div class="lp-glass-card">
                <div class="lp-glass-head">
                  <div class="lp-glass-head-icon"><v-icon color="#fff">mdi-tent</v-icon></div>
                  <div>
                    <div class="lp-glass-title">Popular Camps</div>
                    <div class="lp-glass-sub">Western Ghats, Kerala</div>
                  </div>
                  <span class="lp-glass-live"><i></i> Live</span>
                </div>
                <div class="lp-glass-row" v-for="c in heroCamps" :key="c.name">
                  <div class="lp-glass-row-icon" :style="{ background: c.gradient }">
                    <v-icon color="#fff" size="22">{{ c.icon }}</v-icon>
                  </div>
                  <div class="lp-glass-row-body">
                    <div class="lp-glass-row-name">{{ c.name }}</div>
                    <div class="lp-glass-row-loc"><v-icon x-small color="rgba(255,255,255,0.6)">mdi-map-marker</v-icon> {{ c.loc }}</div>
                  </div>
                  <span class="lp-glass-avail" :class="{ 'lp-glass-avail--few': c.few }">{{ c.status }}</span>
                </div>
                <router-link to="/login" class="lp-glass-cta">
                  View all camps <v-icon small color="#fff" style="margin-left:6px;">mdi-arrow-right</v-icon>
                </router-link>
              </div>
              <div class="lp-glass-chip lp-glass-chip--bottom">
                <v-icon small color="#4ade80">mdi-leaf</v-icon> 100% Eco-Friendly
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Scroll arrow -->
      <a href="#features" class="lp-scroll-arrow">
        <v-icon color="rgba(255,255,255,0.7)" size="32">mdi-chevron-double-down</v-icon>
      </a>
    </section>

    <!-- ─── GOVT STRIP ─── -->
    <div class="lp-gov-strip">
      <div class="lp-gov-strip-inner">
        <div class="lp-gov-strip-badge">
          <v-icon color="#13736f" size="20">mdi-shield-star</v-icon>
          <span>Official Portal</span>
        </div>
        <span class="lp-gov-strip-text">
          An initiative by the <strong>Kerala Forest Department</strong>, Government of Kerala, to promote eco-tourism and sustainable nature experiences across 14 districts.
        </span>
        <div class="lp-gov-strip-badge">
          <v-icon color="#d97706" size="20">mdi-leaf</v-icon>
          <span>Eco-Certified</span>
        </div>
      </div>
    </div>

    <!-- ─── FEATURES ─── -->
    <section class="lp-features" id="features">
      <div class="lp-section-inner">
        <div class="lp-section-head reveal">
          <div class="lp-pill">
            <v-icon x-small color="#13736f">mdi-star-four-points</v-icon>
            Why NATURESTAY
          </div>
          <h2 class="lp-section-title">Everything You Need<br/>for the Perfect Forest Escape</h2>
          <p class="lp-section-sub">Seamlessly bridging the wild and the digital — our platform makes Kerala's forests accessible to everyone.</p>
        </div>

        <div class="lp-feat-grid">
          <div class="lp-feat-card reveal" v-for="(f, i) in features" :key="f.title" :style="{ transitionDelay: i * 80 + 'ms' }">
            <div class="lp-feat-icon-wrap" :style="{ '--icon-bg': f.bg, '--icon-color': f.color }">
              <v-icon :color="f.color" size="26">{{ f.icon }}</v-icon>
            </div>
            <div class="lp-feat-body">
              <h3 class="lp-feat-title">{{ f.title }}</h3>
              <p class="lp-feat-desc">{{ f.desc }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── SHOWCASE / ABOUT ─── -->
    <section class="lp-about" id="about">
      <div class="lp-section-inner">
        <div class="lp-about-grid">
          <!-- Visual -->
          <div class="lp-about-visual reveal reveal--left">
            <div class="lp-vis-main">
              <img src="../assets/Images/kerala_wayanad_camp.jpg" alt="Wayanad Nature Camp" class="lp-vis-img" />
            </div>
            <div class="lp-vis-card lp-vis-card--tl">
              <v-icon color="#13736f" size="22">mdi-pine-tree</v-icon>
              <div>
                <div class="lp-vis-card-num">50+</div>
                <div class="lp-vis-card-lbl">Nature Camps</div>
              </div>
            </div>
            <div class="lp-vis-card lp-vis-card--br">
              <v-icon color="#d97706" size="22">mdi-map-marker-multiple</v-icon>
              <div>
                <div class="lp-vis-card-num">14</div>
                <div class="lp-vis-card-lbl">Districts</div>
              </div>
            </div>
            <div class="lp-vis-kfd">
              <img src="../assets/Images/pnglogo.png" alt="" class="lp-vis-kfd-logo" />
              <span>Nature Camp</span>
            </div>
          </div>

          <!-- Text -->
          <div class="lp-about-text reveal reveal--right">
            <div class="lp-pill lp-pill--gold">
              <v-icon x-small color="#d97706">mdi-pine-tree</v-icon>
              About NATURESTAY
            </div>
            <h2 class="lp-about-title">Kerala's Official Nature Camp Reservation System</h2>
            <p class="lp-about-desc">
              NATURESTAY is the <strong>official digital portal</strong> of the Kerala Forest Department for discovering, managing, and booking nature camp experiences across the state. Built to connect nature enthusiasts with Kerala's extraordinary ecological heritage.
            </p>

            <div class="lp-about-points">
              <div class="lp-about-pt" v-for="pt in points" :key="pt.text">
                <div class="lp-about-check">
                  <v-icon small color="#13736f">mdi-check-bold</v-icon>
                </div>
                <div>
                  <div class="lp-about-pt-title">{{ pt.title }}</div>
                  <div class="lp-about-pt-desc">{{ pt.desc }}</div>
                </div>
              </div>
            </div>

            <div class="lp-about-cta">
              <router-link to="/register" class="lp-btn-primary">
                <v-icon style="margin-right:6px;">mdi-account-plus-outline</v-icon>
                Register Now
              </router-link>
              <router-link to="/login" class="lp-btn-teal-outline">
                Sign In
                <v-icon style="margin-left:6px;" small>mdi-arrow-right</v-icon>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── CAMPS GRID ─── -->
    <section class="lp-camps-section">
      <div class="lp-section-inner">
        <div class="lp-section-head reveal">
          <div class="lp-pill">
            <v-icon x-small color="#13736f">mdi-map-marker</v-icon>
            Explore Camps
          </div>
          <h2 class="lp-section-title">Handpicked Experiences<br/>Across Kerala</h2>
        </div>
        <div class="lp-camps-grid">
          <div class="lp-camp-card reveal" v-for="(camp, i) in camps" :key="camp.name" :style="{ transitionDelay: i * 100 + 'ms' }">
            <div class="lp-camp-img-wrap">
              <div class="lp-camp-bg" :style="{ background: camp.gradient }"></div>
              <v-icon class="lp-camp-icon" size="48" color="rgba(255,255,255,0.9)">{{ camp.icon }}</v-icon>
            </div>
            <div class="lp-camp-body">
              <h4 class="lp-camp-name">{{ camp.name }}</h4>
              <p class="lp-camp-desc">{{ camp.desc }}</p>
              <div class="lp-camp-tags">
                <span class="lp-camp-tag" v-for="tag in camp.tags" :key="tag">{{ tag }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── HOW IT WORKS ─── -->
    <section class="lp-steps-section">
      <div class="lp-section-inner">
        <div class="lp-section-head reveal">
          <div class="lp-pill lp-pill--gold">
            <v-icon x-small color="#d97706">mdi-routes</v-icon>
            How It Works
          </div>
          <h2 class="lp-section-title">From Browsing to Camping<br/>in Four Simple Steps</h2>
        </div>
        <div class="lp-steps">
          <div class="lp-step reveal" v-for="(s, i) in steps" :key="s.title" :style="{ transitionDelay: i * 120 + 'ms' }">
            <div class="lp-step-num">{{ i + 1 }}</div>
            <div class="lp-step-icon"><v-icon color="#13736f" size="30">{{ s.icon }}</v-icon></div>
            <h4 class="lp-step-title">{{ s.title }}</h4>
            <p class="lp-step-desc">{{ s.desc }}</p>
          </div>
        </div>
        <div class="reveal" style="text-align:center; margin-top:36px;">
          <router-link to="/how-to-apply" class="lp-btn-outline-green">
            <v-icon small style="margin-right:8px;" color="#13736f">mdi-file-document-multiple-outline</v-icon>
            View Document Requirements &amp; Application Guide
            <v-icon small style="margin-left:6px;" color="#13736f">mdi-arrow-right</v-icon>
          </router-link>
        </div>
      </div>
    </section>

    <!-- ─── CTA BANNER ─── -->
    <section class="lp-cta-section" id="contact">
      <div class="lp-cta-bg">
        <img src="../assets/Images/kerala_login_bg.jpg" alt="" class="lp-cta-img" />
        <div class="lp-cta-overlay"></div>
      </div>
      <div class="lp-cta-content">
        <div class="lp-gov-banner lp-gov-banner--center">
          <span class="lp-kfd-badge lp-kfd-badge--cta"><img src="../assets/Images/blacklogo.png" alt="Kerala Forests" /></span>
          <div class="lp-gov-text">
            <span class="lp-gov-title" style="font-size:14px;">Kerala Forest Department</span>
            <span class="lp-gov-sub">Government of Kerala</span>
          </div>
        </div>
        <h2 class="lp-cta-title">Ready to Experience<br/>the Wilderness?</h2>
        <p class="lp-cta-sub">Join thousands who have already discovered Kerala's extraordinary forest heritage through our official portal.</p>
        <div class="lp-cta-btns">
          <router-link to="/login" class="lp-btn-primary lp-btn-xl">
            <v-icon style="margin-right:10px;">mdi-tent</v-icon>
            Book Your Camp Now
          </router-link>
          <router-link to="/register" class="lp-btn-white lp-btn-xl">
            <v-icon style="margin-right:8px;color:#13736f;">mdi-account-plus</v-icon>
            Create an Account
          </router-link>
        </div>
      </div>
    </section>

    <!-- ─── FOOTER ─── -->
    <footer class="lp-footer">
      <div class="lp-footer-inner">
        <div class="lp-footer-brand">
          <span class="lp-kfd-badge lp-kfd-badge--footer"><img src="../assets/Images/blacklogo.png" alt="Kerala Forests" /></span>
          <img src="../assets/Images/pnglogo.png" alt="" class="lp-footer-logo" />
          <div>
            <div class="lp-footer-name">NATURESTAY</div>
            <div class="lp-footer-dept">Kerala Forest Department</div>
            <div class="lp-footer-gov">Government of Kerala</div>
          </div>
        </div>
        <div class="lp-footer-links">
          <router-link to="/login" class="lp-footer-link">Sign In</router-link>
          <router-link to="/register" class="lp-footer-link">Register</router-link>
          <router-link to="/how-to-apply" class="lp-footer-link">How to Apply</router-link>
          <a href="#features" class="lp-footer-link">Features</a>
          <a href="#about" class="lp-footer-link">About</a>
        </div>
        <div class="lp-footer-copy">
          <p>© {{ year }} Kerala Forest Department, Government of Kerala.</p>
          <p class="lp-footer-dev">Developed &amp; Managed by <strong>Leopard Tech Labs Pvt. Ltd.</strong></p>
        </div>
      </div>
    </footer>

  </div>
</template>

<script>
export default {
  name: "LandingPage",
  data() {
    return {
      scrolled: false,
      menuOpen: false,
      stats: [
        { icon: "mdi-tent", color: "#fbbf24", value: "50+", label: "Nature Camps" },
        { icon: "mdi-map-marker", color: "#34d399", value: "14", label: "Districts" },
        { icon: "mdi-account-group", color: "#60a5fa", value: "10K+", label: "Visitors" },
        { icon: "mdi-leaf", color: "#a78bfa", value: "100%", label: "Eco-Friendly" },
      ],
      features: [
        { icon: "mdi-calendar-check-outline", title: "Easy Online Booking", desc: "Reserve your nature camp slot in minutes with our streamlined digital booking system.", bg: "rgba(19,115,111,0.1)", color: "#13736f" },
        { icon: "mdi-map-search-outline", title: "50+ Camp Locations", desc: "Curated nature camps spread across Kerala's stunning landscapes, hills, and forests.", bg: "rgba(251,191,36,0.12)", color: "#d97706" },
        { icon: "mdi-shield-check-outline", title: "Official & Verified", desc: "All bookings managed through the official Kerala Forest Department portal.", bg: "rgba(34,197,94,0.1)", color: "#16a34a" },
        { icon: "mdi-clock-fast", title: "Real-Time Availability", desc: "Check live camp availability and get instant confirmation for your chosen dates.", bg: "rgba(99,102,241,0.1)", color: "#6366f1" },
        { icon: "mdi-account-group-outline", title: "Group & Family Bookings", desc: "Flexible booking options for families, school groups, and corporate outings.", bg: "rgba(239,68,68,0.1)", color: "#dc2626" },
        { icon: "mdi-cash-check", title: "Digital Payments", desc: "Secure integrated payment gateway with instant receipts and booking confirmation.", bg: "rgba(16,185,129,0.1)", color: "#059669" },
      ],
      points: [
        { title: "Official Government Portal", desc: "Operated directly by the Kerala Forest Department" },
        { title: "Transparent Process", desc: "Clear application, approval, and payment workflow" },
        { title: "Multi-Role Access", desc: "For users, clerks, division officers, and admins" },
        { title: "Eco-Tourism Focused", desc: "Promotes sustainable, responsible nature tourism" },
        { title: "Real-time Status Tracking", desc: "Track your application status at every stage" },
      ],
      heroCamps: [
        { name: "Forest Trekking Camp", loc: "Wayanad", icon: "mdi-hiking", gradient: "linear-gradient(135deg,#1b4332,#2d6a4f)", status: "Available", few: false },
        { name: "Riverside Waterfall Stay", loc: "Thekkady", icon: "mdi-waves", gradient: "linear-gradient(135deg,#0c4f4c,#13736f)", status: "Few slots", few: true },
        { name: "Wildlife Observation", loc: "Parambikulam", icon: "mdi-elephant", gradient: "linear-gradient(135deg,#7b2d00,#b45309)", status: "Available", few: false },
      ],
      steps: [
        { icon: "mdi-account-plus-outline", title: "Create an Account", desc: "Register in under a minute with your email." },
        { icon: "mdi-map-search-outline", title: "Choose a Camp", desc: "Browse camps, check dates and availability." },
        { icon: "mdi-file-document-check-outline", title: "Apply & Get Approved", desc: "Submit your application and track approval live." },
        { icon: "mdi-tent", title: "Pay & Enjoy", desc: "Confirm with secure payment and head to the forest." },
      ],
      camps: [
        { name: "Forest Trekking Camps", desc: "Guided treks through protected forest reserves with expert naturalists.", icon: "mdi-hiking", gradient: "linear-gradient(135deg, #1b4332, #2d6a4f)", tags: ["Trekking", "Wildlife", "Adventure"] },
        { name: "River & Waterfall Stays", desc: "Camp beside pristine rivers and cascading waterfalls in Kerala's Western Ghats.", icon: "mdi-waves", gradient: "linear-gradient(135deg, #0c4f4c, #13736f)", tags: ["Rivers", "Waterfalls", "Scenic"] },
        { name: "Wildlife Observation", desc: "Night safaris and wildlife observation zones in designated buffer areas.", icon: "mdi-elephant", gradient: "linear-gradient(135deg, #7b2d00, #b45309)", tags: ["Safari", "Wildlife", "Photography"] },
        { name: "Heritage Forest Camps", desc: "Explore the cultural and ecological heritage of ancient Kerala forest settlements.", icon: "mdi-home-city-outline", gradient: "linear-gradient(135deg, #1e3a5f, #2563eb)", tags: ["Heritage", "Culture", "Eco"] },
      ],
    };
  },
  computed: {
    year() { return new Date().getFullYear(); },
  },
  mounted() {
    window.addEventListener("scroll", this.onScroll);
    const els = this.$el.querySelectorAll(".reveal");
    if (!("IntersectionObserver" in window)) {
      els.forEach((el) => el.classList.add("reveal--in"));
      return;
    }
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add("reveal--in");
          this.observer.unobserve(e.target);
        }
      });
    }, { threshold: 0.12 });
    els.forEach((el) => this.observer.observe(el));
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.onScroll);
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    onScroll() { this.scrolled = window.scrollY > 40; },
    particleStyle(n) {
      const size = 10 + (n % 5) * 4;
      const delay = (n * 1.3) % 8;
      const duration = 15 + (n * 2.1) % 12;
      const left = ((n * 37) % 95) + 2;
      return {
        left: left + "vw",
        animationDelay: delay + "s",
        animationDuration: duration + "s",
        fontSize: size + "px",
        opacity: 0.12 + (n % 4) * 0.04,
      };
    },
  },
};
</script>

<style scoped>
/* Body text uses the shared DM Sans token (headings keep their own font) */
.lp-root p, .lp-root li, .lp-root label, .lp-root small, .lp-root input, .lp-root textarea, .lp-root select, .lp-root button { font-family: var(--font-body); }


/* ─── Root ──────────────────────────────────── */
.lp-root {
  font-family: var(--font-body);
  background: #fff;
  overflow-x: hidden;
}

/* ─── Particles ─────────────────────────────── */
.particles {
  position: fixed; inset: 0;
  pointer-events: none; z-index: 0;
  overflow: hidden;
}
.particle {
  position: absolute;
  bottom: -60px;
  animation: floatUp linear infinite;
  user-select: none;
}
@keyframes floatUp {
  0%   { transform: translateY(0) rotate(0deg); opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 0.5; }
  100% { transform: translateY(-110vh) rotate(360deg); opacity: 0; }
}

/* ─── NAVBAR ─────────────────────────────────── */
.lp-nav {
  position: fixed; top: 0; left: 0; right: 0; z-index: 200;
  padding: 0 24px;
  background: linear-gradient(180deg, rgba(8,46,44,0.98) 0%, rgba(8,46,44,0.85) 100%);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  transition: background 0.3s, box-shadow 0.3s;
}
.lp-nav--scrolled {
  background: rgba(8,46,44,0.99) !important;
  box-shadow: 0 4px 32px rgba(0,0,0,0.25);
}
.lp-nav-inner {
  max-width: 1540px; margin: 0 auto; width: 100%;
  height: 68px;
  display: flex; align-items: center; justify-content: space-between;
}
.lp-nav-brand { display: flex; align-items: center; gap: 12px; text-decoration: none; }
.lp-nav-logo { width: 42px; height: 42px; object-fit: contain; filter: drop-shadow(0 2px 8px rgba(0,0,0,0.2)); }
.lp-nav-brand-text { display: flex; flex-direction: column; }
.lp-nav-name { font-size: 17px; font-weight: 900; color: #fff; letter-spacing: 1.5px; line-height: 1.1; }
.lp-nav-dept { font-size: 10px; font-weight: 600; color: rgba(255,255,255,0.55); letter-spacing: 0.5px; text-transform: uppercase; }

.lp-nav-links { display: flex; align-items: center; gap: 28px; }
.lp-nav-link {
  font-size: 14px; font-weight: 600;
  color: rgba(255,255,255,0.78); text-decoration: none;
  transition: color 0.2s; position: relative;
}
.lp-nav-link::after {
  content: ''; position: absolute; bottom: -2px; left: 0; right: 0;
  height: 2px; background: #fbbf24; border-radius: 2px;
  transform: scaleX(0); transition: transform 0.2s;
}
.lp-nav-link:hover { color: #fff; }
.lp-nav-link:hover::after { transform: scaleX(1); }
.lp-nav-btn {
  display: inline-flex; align-items: center;
  padding: 9px 20px; border-radius: 10px;
  background: linear-gradient(135deg, #13736f, #2a9d8f);
  color: #fff; font-size: 14px; font-weight: 700;
  text-decoration: none; transition: all 0.25s;
  box-shadow: 0 4px 14px rgba(19,115,111,0.4);
  border: none;
}
.lp-nav-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(19,115,111,0.5); }

/* ─── HERO ───────────────────────────────────── */
.lp-hero {
  position: relative;
  height: auto; min-height: max(700px, 100vh);
  display: flex; align-items: center;
  overflow: hidden;
}
.lp-hero-bg { position: absolute; inset: 0; }
.lp-hero-img { width: 100%; height: 100%; object-fit: cover; transform-origin: center; animation: zoomBg 20s ease-in-out infinite alternate; }
@keyframes zoomBg { from { transform: scale(1); } to { transform: scale(1.06); } }
.lp-hero-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(120deg,
    rgba(4,30,28,0.94) 0%,
    rgba(8,50,48,0.82) 40%,
    rgba(0,20,20,0.55) 70%,
    rgba(0,0,0,0.3) 100%);
}
.lp-hero-vignette {
  position: absolute; inset: 0;
  background: radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.5) 100%);
}

.lp-hero-content {
  position: relative; z-index: 2;
  max-width: 1540px; margin: 0 auto;
  padding: calc(68px + clamp(32px, 6vh, 64px)) clamp(20px, 3.5vw, 48px) 60px;
  width: 100%;
}

/* Hero 2-column grid */
.lp-hero-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
  min-height: calc(100vh - 160px);
}

.lp-hero-text {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.lp-hero-visual {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Floating image card in hero */
.lp-hero-img-card {
  position: relative;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 32px 80px rgba(0,0,0,0.5);
  border: 2px solid rgba(255,255,255,0.15);
  width: 100%;
  max-width: 520px;
  animation: floatCard 6s ease-in-out infinite alternate;
}
@keyframes floatCard {
  from { transform: translateY(0px) rotate(-1deg); }
  to   { transform: translateY(-16px) rotate(0deg); }
}

.lp-hero-img-card-photo {
  width: 100%; height: 380px;
  object-fit: cover; display: block;
}

.lp-hero-img-badge {
  position: absolute; bottom: 16px; left: 16px;
  display: inline-flex; align-items: center; gap: 6px;
  background: rgba(255,255,255,0.95);
  border-radius: 10px; padding: 8px 14px;
  font-size: 12px; font-weight: 700; color: #0c4f4c;
  backdrop-filter: blur(8px);
}

.lp-hero-img-tag {
  position: absolute; top: 14px; right: 14px;
  display: inline-flex; align-items: center;
  background: rgba(12,79,76,0.9);
  border-radius: 50px; padding: 5px 12px;
  font-size: 11px; font-weight: 700; color: #fbbf24;
  backdrop-filter: blur(6px);
  border: 1px solid rgba(251,191,36,0.3);
}

/* Government banner inside hero */
.lp-gov-banner {
  display: inline-flex; align-items: center; gap: 10px;
  align-self: flex-start; width: fit-content; max-width: 100%; box-sizing: border-box;
  background: rgba(255,255,255,0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.18);
  border-radius: 50px;
  padding: 6px 18px 6px 6px;
  margin-bottom: 24px;
}
.lp-gov-banner--center { margin: 0 auto 28px; display: flex; align-self: center; }
.lp-gov-logo { width: 38px; height: 38px; object-fit: contain; }
.lp-gov-text { display: flex; flex-direction: column; min-width: 0; gap: 2px; }
.lp-gov-title { font-size: 13px; font-weight: 700; color: #fbbf24; letter-spacing: 0.3px; line-height: 1.2; }
.lp-gov-sub { font-size: 11px; color: rgba(255,255,255,0.7); font-weight: 500; line-height: 1.3; }

/* Headline */
.lp-hero-title {
  display: flex; flex-direction: column;
  margin: 0 0 24px;
}
.lp-title-line1 {
  font-size: clamp(18px, 3vw, 28px);
  font-weight: 700; color: #fbbf24;
  letter-spacing: 6px; text-transform: uppercase;
  margin-bottom: 4px;
}
.lp-title-line2 {
  font-family: var(--font-body);
  font-size: clamp(52px, 9vw, 96px);
  font-weight: 900; line-height: 0.95;
  color: #fff;
  text-shadow: 0 4px 40px rgba(0,0,0,0.3);
}
.lp-title-line2 em { color: #4ade80; font-style: italic; }

.lp-hero-desc {
  font-size: 17px; color: rgba(255,255,255,0.82);
  max-width: 560px; line-height: 1.75; margin: 0 0 36px;
  font-weight: 400;
}

/* CTA buttons */
.lp-hero-cta { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 52px; }

.lp-btn-primary {
  display: inline-flex; align-items: center;
  padding: 15px 30px; border-radius: 12px;
  background: linear-gradient(135deg, #13736f, #2a9d8f);
  color: #fff; font-family: var(--font-body);
  font-size: 15px; font-weight: 700;
  text-decoration: none; border: none; cursor: pointer;
  transition: all 0.25s;
  box-shadow: 0 6px 28px rgba(19,115,111,0.45);
}
.lp-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 36px rgba(19,115,111,0.55); filter: brightness(1.08); }

.lp-btn-ghost {
  display: inline-flex; align-items: center;
  padding: 15px 30px; border-radius: 12px;
  background: rgba(255,255,255,0.1);
  border: 1.5px solid rgba(255,255,255,0.3);
  color: #fff; font-family: var(--font-body);
  font-size: 15px; font-weight: 600;
  text-decoration: none; transition: all 0.25s;
}
.lp-btn-ghost:hover { background: rgba(255,255,255,0.18); border-color: rgba(255,255,255,0.5); }

.lp-btn-xl { padding: 17px 36px; font-size: 16px; border-radius: 14px; }

.lp-btn-teal-outline {
  display: inline-flex; align-items: center;
  padding: 13px 26px; border-radius: 12px;
  border: 2px solid #13736f; background: transparent;
  color: #13736f; font-family: var(--font-body);
  font-size: 14px; font-weight: 700;
  text-decoration: none; transition: all 0.2s;
}
.lp-btn-teal-outline:hover { background: rgba(19,115,111,0.08); }

.lp-btn-white {
  display: inline-flex; align-items: center;
  padding: 15px 30px; border-radius: 12px;
  background: #fff; color: #0c4f4c;
  font-family: var(--font-body);
  font-size: 15px; font-weight: 700;
  text-decoration: none; transition: all 0.25s;
  border: 2px solid rgba(255,255,255,0.6);
}
.lp-btn-white:hover { background: #f0fdf4; transform: translateY(-2px); }

.lp-btn-outline-green {
  display: inline-flex; align-items: center;
  padding: 13px 28px; border-radius: 50px;
  background: #ffffff; color: #13736f;
  font-family: var(--font-body);
  font-size: 14.5px; font-weight: 700;
  text-decoration: none; border: 2px solid rgba(19,115,111,0.3);
  box-shadow: 0 4px 16px rgba(19,115,111,0.08);
  transition: all 0.25s;
}
.lp-btn-outline-green:hover {
  background: #f0fdf4; border-color: #13736f;
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(19,115,111,0.18);
}

/* Stats row */
.lp-stats-row {
  display: flex; flex-wrap: wrap; gap: 12px;
}
.lp-stat-pill {
  display: flex; align-items: center; gap: 8px;
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,0.15);
  border-radius: 50px; padding: 8px 18px;
}
.lp-stat-val { font-size: 18px; font-weight: 800; color: #fff; }
.lp-stat-lbl { font-size: 12px; color: rgba(255,255,255,0.65); font-weight: 500; }

/* Scroll arrow */
.lp-scroll-arrow {
  position: absolute; bottom: 32px; left: 50%;
  transform: translateX(-50%);
  text-decoration: none;
  animation: bounce 2.2s ease-in-out infinite;
}
@keyframes bounce {
  0%,100% { transform: translateX(-50%) translateY(0); }
  50% { transform: translateX(-50%) translateY(10px); }
}

/* ─── GOVT STRIP ─────────────────────────────── */
.lp-gov-strip {
  background: linear-gradient(90deg, #f0fdf4 0%, #d1fae5 50%, #f0fdf4 100%);
  border-top: 2px solid rgba(19,115,111,0.15);
  border-bottom: 2px solid rgba(19,115,111,0.15);
  padding: 18px 24px;
}
.lp-gov-strip-inner {
  max-width: 1540px; margin: 0 auto; width: 100%;
  display: flex; align-items: center; gap: 20px;
  flex-wrap: wrap; justify-content: center;
}
.lp-gov-strip-badge {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 700;
  color: #13736f; white-space: nowrap;
  background: rgba(19,115,111,0.1);
  padding: 6px 14px; border-radius: 50px;
  border: 1px solid rgba(19,115,111,0.2);
}
.lp-gov-strip-text {
  font-size: 14px; color: #374151; text-align: center; line-height: 1.6;
}
.lp-gov-strip-text strong { color: #0c4f4c; }

/* ─── SHARED SECTION STYLES ──────────────────── */
.lp-section-inner {
  max-width: 1540px; margin: 0 auto; padding: 72px clamp(20px, 3.5vw, 48px); width: 100%;
}
.lp-section-head { text-align: center; margin-bottom: 60px; }

.lp-pill {
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(19,115,111,0.1);
  border: 1px solid rgba(19,115,111,0.2);
  border-radius: 50px; padding: 6px 16px;
  font-size: 12px; font-weight: 700; color: #13736f;
  letter-spacing: 0.04em; margin-bottom: 16px; text-transform: uppercase;
}
.lp-pill--gold {
  background: rgba(217,119,6,0.1);
  border-color: rgba(217,119,6,0.25);
  color: #d97706;
}

.lp-section-title {
  font-size: clamp(28px, 4vw, 44px);
  font-weight: 800; color: #0f172a;
  margin: 0 0 14px; line-height: 1.15;
  letter-spacing: -0.5px;
}
.lp-section-sub {
  font-size: 16px; color: #64748b;
  max-width: 580px; margin: 0 auto; line-height: 1.7;
}

/* ─── FEATURES ───────────────────────────────── */
.lp-features { background: #fafafa; }

.lp-feat-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 22px;
}
@media (max-width: 900px) { .lp-feat-grid { grid-template-columns: repeat(2,1fr); } }
@media (max-width: 540px) { .lp-feat-grid { grid-template-columns: 1fr; } }

.lp-feat-card {
  background: #fff;
  border-radius: 20px;
  padding: 28px 24px;
  border: 1px solid #e2e8f0;
  display: flex; gap: 18px; align-items: flex-start;
  transition: all 0.3s;
  cursor: default;
}
.lp-feat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 16px 48px rgba(12,79,76,0.12);
  border-color: rgba(19,115,111,0.25);
}
.lp-feat-icon-wrap {
  width: 54px; height: 54px; flex-shrink: 0;
  border-radius: 14px;
  background: var(--icon-bg);
  display: flex; align-items: center; justify-content: center;
  transition: transform 0.3s;
}
.lp-feat-card:hover .lp-feat-icon-wrap { transform: scale(1.1) rotate(-5deg); }
.lp-feat-body { min-width: 0; }
.lp-feat-title { font-size: 15px; font-weight: 700; color: #0f172a; margin: 0 0 6px; }
.lp-feat-desc { font-size: 13px; color: #64748b; line-height: 1.6; margin: 0; }

/* ─── ABOUT / SHOWCASE ───────────────────────── */
.lp-about { background: #fff; }

.lp-about-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 72px; align-items: center;
}
@media (max-width: 800px) {
  .lp-about-grid { grid-template-columns: 1fr; gap: 40px; }
  .lp-about-visual { order: -1; }
}

/* Visual side */
.lp-about-visual { position: relative; }
.lp-vis-main {
  border-radius: 24px; overflow: hidden;
  box-shadow: 0 24px 72px rgba(12,79,76,0.18);
}
.lp-vis-img { width: 100%; height: 420px; object-fit: cover; display: block; }

.lp-vis-card {
  position: absolute;
  background: #fff;
  border-radius: 16px; padding: 14px 18px;
  box-shadow: 0 8px 36px rgba(0,0,0,0.12);
  display: flex; align-items: center; gap: 10px;
}
.lp-vis-card--tl { top: -18px; left: -18px; }
.lp-vis-card--br { bottom: 56px; right: -18px; }
.lp-vis-card-num { font-size: 22px; font-weight: 900; color: #0c4f4c; }
.lp-vis-card-lbl { font-size: 11px; color: #64748b; font-weight: 600; }

.lp-vis-kfd {
  position: absolute; bottom: -18px; left: 50%; transform: translateX(-50%);
  background: linear-gradient(135deg, #0c4f4c, #13736f);
  border-radius: 50px; padding: 10px 20px;
  display: flex; align-items: center; gap: 8px;
  color: #fff; font-size: 12px; font-weight: 700;
  white-space: nowrap;
  box-shadow: 0 6px 24px rgba(12,79,76,0.3);
}
.lp-vis-kfd-logo { width: 24px; height: 24px; object-fit: contain; }

/* Text side */
.lp-about-title {
  font-size: clamp(24px, 3.5vw, 36px);
  font-weight: 800; color: #0f172a;
  margin: 0 0 18px; line-height: 1.2;
  letter-spacing: -0.3px;
}
.lp-about-desc {
  font-size: 15px; color: #475569;
  line-height: 1.75; margin: 0 0 28px;
}
.lp-about-desc strong { color: #0c4f4c; }

.lp-about-points { display: flex; flex-direction: column; gap: 16px; margin-bottom: 32px; }
.lp-about-pt { display: flex; align-items: flex-start; gap: 12px; }
.lp-about-check {
  width: 30px; height: 30px; flex-shrink: 0;
  background: rgba(19,115,111,0.1);
  border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  margin-top: 2px;
}
.lp-about-pt-title { font-size: 14px; font-weight: 700; color: #0f172a; margin-bottom: 2px; }
.lp-about-pt-desc { font-size: 12px; color: #64748b; line-height: 1.5; }
.lp-about-cta { display: flex; gap: 12px; flex-wrap: wrap; }

/* ─── CAMPS GRID ─────────────────────────────── */
.lp-camps-section { background: #f8fafc; }

.lp-camps-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
}
@media (max-width: 960px) { .lp-camps-grid { grid-template-columns: repeat(2,1fr); } }
@media (max-width: 540px) { .lp-camps-grid { grid-template-columns: 1fr; } }

.lp-camp-card {
  background: #fff;
  border-radius: 20px; overflow: hidden;
  border: 1px solid #e2e8f0;
  transition: all 0.3s;
}
.lp-camp-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 20px 56px rgba(12,79,76,0.14);
}
.lp-camp-img-wrap {
  height: 140px; position: relative;
  display: flex; align-items: center; justify-content: center;
}
.lp-camp-bg { position: absolute; inset: 0; }
.lp-camp-icon { position: relative; z-index: 1; filter: drop-shadow(0 2px 8px rgba(0,0,0,0.2)); }
.lp-camp-body { padding: 18px 18px 20px; }
.lp-camp-name { font-size: 15px; font-weight: 700; color: #0f172a; margin: 0 0 6px; }
.lp-camp-desc { font-size: 12px; color: #64748b; line-height: 1.6; margin: 0 0 12px; }
.lp-camp-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.lp-camp-tag {
  font-size: 10px; font-weight: 700;
  background: rgba(19,115,111,0.08);
  border: 1px solid rgba(19,115,111,0.15);
  color: #13736f; border-radius: 50px;
  padding: 2px 8px;
}

/* ─── CTA SECTION ────────────────────────────── */
.lp-cta-section {
  position: relative;
  padding: 100px 28px;
  text-align: center;
  overflow: hidden;
}
.lp-cta-bg { position: absolute; inset: 0; }
.lp-cta-img { width: 100%; height: 100%; object-fit: cover; }
.lp-cta-overlay {
  position: absolute; inset: 0;
  background: linear-gradient(135deg, rgba(4,30,28,0.95) 0%, rgba(12,79,76,0.9) 100%);
}
.lp-cta-content {
  position: relative; z-index: 2;
  max-width: 740px; margin: 0 auto;
  display: flex; flex-direction: column; align-items: center;
}
.lp-cta-title {
  font-family: var(--font-body);
  font-size: clamp(32px, 5vw, 54px);
  font-weight: 900; color: #fff;
  margin: 0 0 16px; line-height: 1.15;
}
.lp-cta-sub {
  font-size: 16px; color: rgba(255,255,255,0.8);
  max-width: 560px; line-height: 1.7; margin: 0 0 38px;
}
.lp-cta-btns { display: flex; gap: 16px; flex-wrap: wrap; justify-content: center; }

/* ─── FOOTER ─────────────────────────────────── */
.lp-footer {
  background: #041e1d;
  padding: 40px 28px;
}
.lp-footer-inner {
  max-width: 1540px; margin: 0 auto; width: 100%;
  display: flex; align-items: center; justify-content: space-between;
  gap: 24px; flex-wrap: wrap;
}
.lp-footer-brand { display: flex; align-items: center; gap: 12px; }
.lp-footer-logo { width: 36px; height: 36px; object-fit: contain; opacity: 0.85; }
.lp-footer-name { font-size: 16px; font-weight: 900; color: #fff; letter-spacing: 1.5px; }
.lp-footer-dept { font-size: 11px; color: rgba(255,255,255,0.6); font-weight: 600; }
.lp-footer-gov { font-size: 10px; color: rgba(255,255,255,0.35); font-weight: 500; }

.lp-footer-links { display: flex; gap: 20px; flex-wrap: wrap; }
.lp-footer-link {
  font-size: 13px; font-weight: 600;
  color: rgba(255,255,255,0.55); text-decoration: none;
  transition: color 0.2s;
}
.lp-footer-link:hover { color: #4ade80; }

.lp-footer-copy { font-size: 11px; color: rgba(255,255,255,0.35); text-align: right; line-height: 1.6; }
.lp-footer-dev { font-size: 10px; color: rgba(255,255,255,0.25); margin-top: 4px; }
.lp-footer-dev strong { color: rgba(255,255,255,0.4); }

/* ─── Hero glass card ────────────────────────── */
.lp-glass-wrap { position: relative; width: 100%; max-width: 440px; padding: 28px 0; }
.lp-glass-card {
  position: relative; z-index: 1;
  background: linear-gradient(160deg, rgba(255,255,255,0.16), rgba(255,255,255,0.05));
  backdrop-filter: blur(22px); -webkit-backdrop-filter: blur(22px);
  border: 1px solid rgba(255,255,255,0.22);
  border-radius: 26px; padding: 22px;
  box-shadow: 0 30px 80px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.25);
  animation: floatCard 6s ease-in-out infinite alternate;
}
.lp-glass-head { display: flex; align-items: center; gap: 12px; margin-bottom: 18px; }
.lp-glass-head-icon {
  width: 46px; height: 46px; border-radius: 14px;
  background: linear-gradient(135deg, #13736f, #2a9d8f);
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 6px 18px rgba(19,115,111,0.5);
}
.lp-glass-title { font-size: 17px; font-weight: 800; color: #fff; }
.lp-glass-sub { font-size: 12px; color: rgba(255,255,255,0.65); font-weight: 500; }
.lp-glass-live {
  margin-left: auto; display: inline-flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 700; color: #4ade80;
  background: rgba(74,222,128,0.12); border: 1px solid rgba(74,222,128,0.3);
  padding: 4px 10px; border-radius: 50px;
}
.lp-glass-live i { width: 7px; height: 7px; border-radius: 50%; background: #4ade80; animation: livePulse 1.6s infinite; }
@keyframes livePulse { 0% { box-shadow: 0 0 0 0 rgba(74,222,128,0.6); } 100% { box-shadow: 0 0 0 8px rgba(74,222,128,0); } }
.lp-glass-row {
  display: flex; align-items: center; gap: 12px;
  padding: 11px 12px; margin-bottom: 10px; border-radius: 16px;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.1);
  transition: transform 0.25s, background 0.25s;
}
.lp-glass-row:hover { transform: translateX(6px); background: rgba(255,255,255,0.15); }
.lp-glass-row-icon { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.lp-glass-row-body { min-width: 0; flex: 1; }
.lp-glass-row-name { font-size: 14px; font-weight: 700; color: #fff; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.lp-glass-row-loc { font-size: 11.5px; color: rgba(255,255,255,0.65); margin-top: 2px; }
.lp-glass-avail {
  font-size: 11px; font-weight: 700; color: #4ade80; white-space: nowrap;
  background: rgba(74,222,128,0.12); border-radius: 50px; padding: 4px 10px;
}
.lp-glass-avail--few { color: #fbbf24; background: rgba(251,191,36,0.14); }
.lp-glass-cta {
  display: flex; align-items: center; justify-content: center;
  margin-top: 6px; padding: 13px; border-radius: 14px;
  background: linear-gradient(135deg, #13736f, #2a9d8f);
  color: #fff; font-size: 14px; font-weight: 700; text-decoration: none;
  box-shadow: 0 6px 20px rgba(19,115,111,0.45); transition: transform 0.2s, filter 0.2s;
}
.lp-glass-cta:hover { transform: translateY(-2px); filter: brightness(1.1); }
.lp-glass-chip {
  position: absolute; z-index: 2; display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; border-radius: 50px;
  background: rgba(8,46,44,0.85); backdrop-filter: blur(10px);
  border: 1px solid rgba(255,255,255,0.18);
  font-size: 12px; font-weight: 700; color: #fff;
  box-shadow: 0 8px 24px rgba(0,0,0,0.35);
}
.lp-glass-chip--top { top: 0; right: -14px; animation: chipBob 5s ease-in-out infinite alternate; }
.lp-glass-chip--bottom { bottom: 0; left: -14px; animation: chipBob 6s ease-in-out infinite alternate-reverse; }
@keyframes chipBob { from { transform: translateY(0); } to { transform: translateY(-8px); } }

/* ─── KFD emblem badge ───────────────────────── */
.lp-kfd-badge {
  display: inline-flex; align-items: center; justify-content: center;
  flex-shrink: 0; border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0,0,0,0.25), 0 0 0 2px rgba(251,191,36,0.55);
}
.lp-kfd-badge img { width: 78%; height: 78%; object-fit: contain; }
.lp-kfd-badge--nav { width: 46px; height: 46px; margin-right: 2px; }
.lp-kfd-badge--hero { width: 38px; height: 38px; }
.lp-kfd-badge--sm { width: 26px; height: 26px; box-shadow: none; }
.lp-kfd-badge--cta { width: 60px; height: 60px; }
.lp-kfd-badge--footer { width: 40px; height: 40px; box-shadow: 0 0 0 2px rgba(251,191,36,0.4); }

/* ─── Visual enhancements ────────────────────── */
.reveal { opacity: 0; transform: translateY(32px); transition: opacity 0.7s ease, transform 0.7s cubic-bezier(.2,.7,.2,1), box-shadow 0.3s, border-color 0.3s; }
.reveal--left { transform: translateX(-40px); }
.reveal--right { transform: translateX(40px); }
.reveal.reveal--in { opacity: 1; transform: none; }
.lp-feat-card.reveal--in:hover { transform: translateY(-5px); }
.lp-camp-card.reveal--in:hover { transform: translateY(-6px); }

/* Hero: glow, gradient headline, shimmering CTA */
.lp-hero-content::before {
  content: ''; position: absolute; top: 10%; left: -10%;
  width: 520px; height: 520px; border-radius: 50%;
  background: radial-gradient(circle, rgba(74,222,128,0.22), transparent 70%);
  filter: blur(40px); pointer-events: none;
  animation: orbDrift 12s ease-in-out infinite alternate;
}
@keyframes orbDrift { from { transform: translate(0,0); } to { transform: translate(60px,40px); } }
.lp-title-line2 em {
  background: linear-gradient(90deg, #4ade80, #fbbf24, #4ade80);
  background-size: 200% auto;
  -webkit-background-clip: text; background-clip: text;
  -webkit-text-fill-color: transparent; color: transparent;
  animation: gradShift 6s linear infinite;
}
@keyframes gradShift { to { background-position: 200% center; } }
.lp-hero-text > * { animation: heroIn 0.9s cubic-bezier(.2,.7,.2,1) both; }
.lp-hero-text > *:nth-child(2) { animation-delay: 0.1s; }
.lp-hero-text > *:nth-child(3) { animation-delay: 0.2s; }
.lp-hero-text > *:nth-child(4) { animation-delay: 0.3s; }
.lp-hero-text > *:nth-child(5) { animation-delay: 0.4s; }
@keyframes heroIn { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: none; } }
.lp-btn-primary { position: relative; overflow: hidden; }
.lp-btn-primary::after {
  content: ''; position: absolute; top: 0; left: -120%;
  width: 60%; height: 100%;
  background: linear-gradient(100deg, transparent, rgba(255,255,255,0.35), transparent);
  transform: skewX(-20deg); animation: shine 3.5s ease-in-out infinite;
}
@keyframes shine { 0%,60% { left: -120%; } 100% { left: 160%; } }
.lp-stat-pill { transition: transform 0.25s, background 0.25s; }
.lp-stat-pill:hover { transform: translateY(-3px); background: rgba(255,255,255,0.18); }
.lp-hero-img-card::after {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background: linear-gradient(180deg, transparent 55%, rgba(4,30,28,0.55));
}
.lp-hero-img-badge, .lp-hero-img-tag { z-index: 2; }

/* Section titles: accent underline */
.lp-section-title::after {
  content: ''; display: block; width: 64px; height: 4px; margin: 16px auto 0;
  border-radius: 4px; background: linear-gradient(90deg, #13736f, #fbbf24);
}

/* Feature cards: gradient top accent */
.lp-feat-card { position: relative; overflow: hidden; }
.lp-feat-card::before {
  content: ''; position: absolute; top: 0; left: 0; right: 0; height: 3px;
  background: linear-gradient(90deg, #13736f, #fbbf24);
  transform: scaleX(0); transform-origin: left; transition: transform 0.4s;
}
.lp-feat-card:hover::before { transform: scaleX(1); }

/* Camp cards: pattern + zoom on hover */
.lp-camp-bg { transition: transform 0.6s; }
.lp-camp-card:hover .lp-camp-bg { transform: scale(1.15); }
.lp-camp-img-wrap::after {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(circle at 20% 20%, rgba(255,255,255,0.22), transparent 45%),
              radial-gradient(circle at 85% 90%, rgba(0,0,0,0.25), transparent 50%);
}
.lp-camp-icon { transition: transform 0.4s; }
.lp-camp-card:hover .lp-camp-icon { transform: scale(1.2) rotate(-6deg); }

/* How it works */
.lp-steps-section { background: linear-gradient(180deg, #fff 0%, #f0fdf4 100%); }
.lp-steps { display: grid; grid-template-columns: repeat(4, 1fr); gap: 28px; position: relative; }
.lp-steps::before {
  content: ''; position: absolute; top: 56px; left: 12%; right: 12%;
  border-top: 2px dashed rgba(19,115,111,0.3);
}
.lp-step {
  position: relative; text-align: center;
  background: #fff; border: 1px solid #e2e8f0; border-radius: 20px;
  padding: 28px 20px 24px;
}
.lp-step:hover { box-shadow: 0 16px 48px rgba(12,79,76,0.12); border-color: rgba(19,115,111,0.3); }
.lp-step-num {
  position: absolute; top: -14px; left: 50%; transform: translateX(-50%);
  width: 30px; height: 30px; border-radius: 50%;
  background: linear-gradient(135deg, #d97706, #fbbf24); color: #fff;
  font-weight: 800; font-size: 14px;
  display: flex; align-items: center; justify-content: center;
  box-shadow: 0 4px 12px rgba(217,119,6,0.4);
}
.lp-step-icon {
  width: 68px; height: 68px; margin: 0 auto 16px; border-radius: 50%;
  background: rgba(19,115,111,0.1);
  display: flex; align-items: center; justify-content: center;
  position: relative; z-index: 1;
}
.lp-step-title { font-size: 15px; font-weight: 700; color: #0f172a; margin: 0 0 6px; }
.lp-step-desc { font-size: 13px; color: #64748b; line-height: 1.6; margin: 0; }
@media (max-width: 900px) {
  .lp-steps { grid-template-columns: repeat(2, 1fr); }
  .lp-steps::before { display: none; }
}
@media (max-width: 540px) { .lp-steps { grid-template-columns: 1fr; } }

/* CTA: animated glow */
.lp-cta-section::before {
  content: ''; position: absolute; z-index: 1; top: -120px; right: -80px;
  width: 420px; height: 420px; border-radius: 50%;
  background: radial-gradient(circle, rgba(251,191,36,0.25), transparent 70%);
  animation: orbDrift 10s ease-in-out infinite alternate; pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .reveal { opacity: 1; transform: none; transition: none; }
  .lp-hero-text > *, .lp-hero-img, .lp-hero-img-card, .particle, .lp-btn-primary::after,
  .lp-title-line2 em, .lp-hero-content::before, .lp-cta-section::before { animation: none; }
}

/* ─── Responsive ─────────────────────────────── */
@media (max-width: 1024px) {
  .lp-hero-grid { grid-template-columns: 1fr; gap: 32px; }
  .lp-hero-visual { display: none !important; }
  .lp-hero-content { padding: 90px 28px 48px; }
  .lp-section-inner { padding: 60px 28px; }
}
.lp-nav-burger {
  display: none; align-items: center; justify-content: center;
  width: 38px; height: 38px; border-radius: 10px; cursor: pointer;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15);
}
.lp-mobile-menu { display: none; }
@media (max-width: 640px) {
  .lp-nav-links .lp-nav-link { display: none; }
  .lp-nav { padding: 0 14px; }
  .lp-nav-inner { height: 60px; gap: 8px; }
  .lp-nav-brand { gap: 8px; min-width: 0; }
  .lp-nav-logo { width: 34px; height: 34px; }
  .lp-nav-name { font-size: 14px; letter-spacing: 1px; }
  .lp-nav-dept { font-size: 8.5px; }
  .lp-nav-links { gap: 8px; flex-shrink: 0; }
  .lp-nav-btn { margin-left: 0; padding: 7px 12px; font-size: 12px; }
  .lp-nav-burger { display: inline-flex; }
  .lp-mobile-menu {
    display: flex; flex-direction: column; gap: 4px;
    padding: 10px 14px 14px;
    background: rgba(8,46,44,0.98); backdrop-filter: blur(20px);
    border-top: 1px solid rgba(255,255,255,0.08);
    box-shadow: 0 12px 28px rgba(0,0,0,0.35);
    margin: 0 -14px;
  }
  .lp-mobile-link {
    display: flex; align-items: center; gap: 12px;
    padding: 12px 14px; border-radius: 10px;
    color: rgba(255,255,255,0.9); font-size: 15px; font-weight: 600; text-decoration: none;
  }
  .lp-mobile-link .v-icon { color: #6ee7b7 !important; }
  .lp-mobile-link:active, .lp-mobile-link.router-link-active { background: rgba(19,115,111,0.35); }
  .lp-menu-enter-active, .lp-menu-leave-active { transition: opacity .2s, transform .2s; }
  .lp-menu-enter, .lp-menu-leave-to { opacity: 0; transform: translateY(-8px); }
  /* hero: let content grow instead of being cropped by fixed 100vh */
  .lp-hero { height: auto; min-height: 100vh; align-items: flex-start; }
  .lp-gov-banner { padding: 5px 14px 5px 5px; gap: 8px; margin-bottom: 18px; }
  .lp-kfd-badge--hero { width: 34px; height: 34px; }
  .lp-gov-title { font-size: 12px; }
  .lp-gov-sub { font-size: 10px; }
  .lp-hero-content { padding: 88px 20px 40px; }
  .lp-stats-row { gap: 8px; }
  .lp-stat-pill { padding: 6px 12px; }
  .lp-section-inner { padding: 48px 16px; }
  .lp-footer-copy { text-align: center; }
  .lp-footer-inner { justify-content: center; text-align: center; }
  .lp-about-grid { gap: 24px; }
  .lp-camps-grid { grid-template-columns: 1fr 1fr; }
}
</style>